import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getManufacturer,
  getManufacturersByIds,
  getManufacturersBySearch,
  getPaginatedManufacturers,
} from "@/api/resources/manufacturers/api";
import i18n from "@/providers/i18n";

import type { GetPaginatedManufacturersProps, GetSearchManufacturersProps } from "@/api/resources/manufacturers/types";

const entity = i18n.t("manufacturer");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("manufacturers");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const manufacturersQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "manufacturers"],
  lists: () => [...manufacturersQueryKeys.all(), "list"],
  listByIds: (ids?: string[]) => [...manufacturersQueryKeys.lists(), "byIds", ids],
  listPaginated: (queryKeyPaginationFilters: GetPaginatedManufacturersProps) => [
    ...manufacturersQueryKeys.lists(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  listBySearch: (searchOptionFilters: GetSearchManufacturersProps) => [
    ...manufacturersQueryKeys.lists(),
    "bySearch",
    searchOptionFilters,
  ],
  byId: (id?: string) => [...manufacturersQueryKeys.all(), "byId", id],
};
export const manufacturersQueries = {
  listByIds: (ids?: string[]) =>
    queryOptions({
      queryKey: [...manufacturersQueryKeys.listByIds(ids)],
      queryFn: () =>
        getManufacturersByIds({
          keyIn: "id",
          idsIn: ids,
        }),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginated: (queryKeyPaginationFilters: GetPaginatedManufacturersProps) =>
    queryOptions({
      queryKey: [...manufacturersQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedManufacturers(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: GetSearchManufacturersProps) =>
    queryOptions({
      queryKey: [...manufacturersQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getManufacturersBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...manufacturersQueryKeys.byId(id)],
      queryFn: id ? () => getManufacturer({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
